// @ts-strict-ignore
import { LabelingArticleConversionDataManager } from '../../components/article/manager/labeling-article-conversion-data.manager';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { MetaElementUsageIdentityVO } from '@shared/vo/meta-element.vo';
import { ConnectionCubeSearchRequirements } from '@article/components/connections/connection-creation/search/connection-cube-search-requirements';
import { MetaElementFeaturePropertyDef } from '../../constants/meta-element-feature-property-def';
import { GroupedCubesSearchRequirements } from '../../model/vo/grouped-cubes-search-requirements';
import { ArticleTypeConstants } from '@article/constants/article-type-constants';
import { PropertyCreator } from '@article/util/property-creator';
import { PropertyValueVO, PropertyVO } from '../../components/article/model/vo/property.vo';
import { PropertyUtils } from '@article/util/property-utils';
import { ArticleConnectionPropertyDef } from '@article/constants/article-connection-property-def';
import { PropertyValueType } from '../../constants/property-value-type';
import { StringUtils } from '@shared/util/string-utils';
import { ArticleCreationManagerFactory } from '../../components/article/manager/article-creation.manager.factory';
import { SearchQueryType } from '../../components/article/model/vo/search-query-type';
import { AccessoryService } from '@article/service/accessory.service';

export class ConvertLabelingArticleCommand {

  constructor(
    private propertyCreator: PropertyCreator,
    public dataManager: LabelingArticleConversionDataManager,
    private containerService: ContainerService,
    private articleCreationManagerFactory: ArticleCreationManagerFactory,
    private accessoryService: AccessoryService
  ) {}

  public async execute(message: {convertToType: string, selectedUsageIds: string[]}): Promise<void> {
    const convertToType: string = message.convertToType;
    const articleUsageIds: string[] = message.selectedUsageIds as string[];
    const articleContainers: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids(articleUsageIds);
    const acceptingCubes: Connection[] = [];
    let searchRequirements: ConnectionCubeSearchRequirements[] = [];

    this.dataManager.collectData(articleUsageIds);

    for (const articleContainer of articleContainers) {
      const parentArticleMetaUsageIds: MetaElementUsageIdentityVO[] = ArticleUsageUtils.getBackwardConnectedConnectionUsageIds(
        articleContainer.articleUsage
      );
      for (const metaUsageId of parentArticleMetaUsageIds) {
        const parentArticleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(metaUsageId.articleUsageId);
        if (!parentArticleContainer) {
          return;
        }
        const acceptingCube: Connection = parentArticleContainer.findFirstConnectionByName(metaUsageId.metaElementName);
        if (!acceptingCube) {
          return;
        }
        acceptingCubes.push(acceptingCube);
      }
      searchRequirements = this.createSearchRequirements(acceptingCubes, convertToType);
    }

    if (convertToType === MetaElementFeaturePropertyDef.TYPE_MARKING_STRIP_PREFIX) {
      await this.dataManager.convertToStrip(this.articleCreationManagerFactory.getNewInstance(), searchRequirements, acceptingCubes, articleUsageIds);
    } else {
      const groupedCubesSearchRequirements: GroupedCubesSearchRequirements[] = [new GroupedCubesSearchRequirements(searchRequirements)];
      this.accessoryService.changeAccessoryViewData({
        groupedCubesSearchRequirements: groupedCubesSearchRequirements,
        searchByArticles: false,
        excludedArticleTypes: ArticleTypeConstants.EXCLUDED_ARTICLE_TYPES,
        detachArticleUsageIds: articleUsageIds,
        searchType: SearchQueryType.ACCESSORY_ARTICLE,
        searchForMultiplePlacementsOfResultsInSingleSearchVectorEnabled: true,
        replaceOperation: undefined,
        replaceArticleUsageIds: undefined,
        includedArticleTypes: undefined
      });
    }
  }

  private createSearchRequirements(
    connectionCubes: Connection[],
    labelingTypePrefix: string
  ): ConnectionCubeSearchRequirements[] {
    const cubesSearchRequirements: ConnectionCubeSearchRequirements[] = this.propertyCreator.createCubesSearchRequirementsByDefault(connectionCubes);
    for (const cubeRequirements of cubesSearchRequirements) {
      // replace existing type-property with newly created type property which contains only required labeling type
      const typeProperty: PropertyVO = PropertyUtils.getPropertyByName(cubeRequirements.connectionRequirements, ArticleConnectionPropertyDef.TYPE);
      if (!typeProperty) {
        continue;
      }
      const labelingTypes: string[] =this.getLabelingTypeByPrefix(typeProperty, labelingTypePrefix);
      if (labelingTypes.length > 0) {
        cubeRequirements.connectionRequirements.splice(cubeRequirements.connectionRequirements.indexOf(typeProperty), 1);
        const labelingTypeProperty: PropertyVO = this.createLabelingTypeProperty(labelingTypes);
        cubeRequirements.connectionRequirements.push(labelingTypeProperty);
      }
    }
    return cubesSearchRequirements;
  }

  private createLabelingTypeProperty(labelingTypes: string[]): PropertyVO {
    const property: PropertyVO = new PropertyVO();
    property.name = ArticleConnectionPropertyDef.TYPE;
    const values: PropertyValueVO[] = [];
    for (const labelingType of labelingTypes) {
      const value: PropertyValueVO = new PropertyValueVO();
      value.type = PropertyValueType.STRING;
      value.stringValue = labelingType;
      values.push(value);
    }
    property.values = values;
    return property;
  }

  private getLabelingTypeByPrefix(typeProperty: PropertyVO, labelingTypePrefix: string): string[] {
    const result: string[] = [];
    if (!typeProperty || !typeProperty.values) {
      return null;
    }
    for (const propertyValue of typeProperty.values) {
      if (propertyValue.type !== PropertyValueType.STRING) {
        continue;
      }
      if (StringUtils.startsWith(propertyValue.stringValue, labelingTypePrefix)) {
        result.push(propertyValue.stringValue);
      }
    }
    return result;
  }
}
